import React, { useMemo } from "react";
import {
  useAddOperationMutation,
  useDeleteOperationMutation,
  useGetOperationsQuery,
  useUpdateOperationMutation,
} from "../../features/operations/operationApi";
import { useLazyPlantOptions } from "../../features/plants/usePlantOptions";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";
import useRelationTypeField from "../../features/relationTypes/useRelationTypeField";
import MasterCrudPage from "../masters/MasterCrudPage";
import { withAuditFilters } from "../masters/filterMetadata";
import { getPlantLabel, renderPlantCell } from "../masters/masterMetadata";

const getOperationQrValue = (row) => row?.qr_value || row?.operation_id;
const getOperationQrDisplayValue = (row) =>
  row?.qr_display_text || row?.operation_id;
const operationFilterFields = withAuditFilters([
  { name: "operation_id", label: "Operation ID", optionSource: "operations" },
  { name: "operation_name", label: "Operation Name" },
  { name: "operation_code", label: "Operation Code" },
  { name: "relation_type", label: "Relation Type" },
]);

const Operation = () => {
  const { selectedPlantId } = usePlantSelection();
  const { field: relationTypeField, loadOptions: loadRelationTypes } = useRelationTypeField(selectedPlantId);
  const {
    options: plantOptions,
    isFetching: isLoadingPlants,
    loadOptions: loadPlantOptions,
  } = useLazyPlantOptions();

  const fields = useMemo(
    () => [
      {
        name: "plant_id",
        label: "Plant",
        type: "select",
        options: plantOptions,
        placeholder: isLoadingPlants ? "Loading plants..." : "Select plant",
        disabled: isLoadingPlants && plantOptions.length === 0,
        disabledOnEdit: true,
        required: true,
        fullWidth: true,
      },
      {
        name: "operation_name",
        label: "Operation Name",
        placeholder: "Enter operation name",
        required: true,
      },
      {
        name: "operation_code",
        label: "Operation Code",
        placeholder: "Enter operation code",
        required: true,
      },
      relationTypeField,
    ],
    [isLoadingPlants, plantOptions, relationTypeField]
  );

  const columns = useMemo(
    () => (metaData) => [
      {
        name: "Operation ID",
        selector: (row) => row?.operation_id || "-",
        sortable: true,
      },
      {
        name: "Operation Name",
        selector: (row) => row?.operation_name || "-",
        sortable: true,
        grow: 2,
      },
      {
        name: "Operation Code",
        selector: (row) => row?.operation_code || "-",
        sortable: true,
      },
      {
        name: "Relation Type",
        selector: (row) => row?.relation_type || "-",
        sortable: true,
      },
      {
        name: "Plant",
        selector: (row) => getPlantLabel(row, metaData),
        cell: (row) => renderPlantCell(row, metaData),
        sortable: true,
        grow: 2,
      },
    ],
    []
  );

  return (
    <MasterCrudPage
      entityName="Operation"
      pageTitle="Operations"
      idField="operation_id"
      displayField="operation_name"
      fields={fields}
      columns={columns}
      searchFields={[
        "operation_id",
        "operation_name",
        "operation_code",
        "relation_type",
        "plant_id",
        (row, metaData) => getPlantLabel(row, metaData),
      ]}
      searchPlaceholder="Search operations"
      useListQuery={useGetOperationsQuery}
      useCreateMutation={useAddOperationMutation}
      useUpdateMutation={useUpdateOperationMutation}
      useDeleteMutation={useDeleteOperationMutation}
      filterFields={operationFilterFields}
      qrDisplayValue={getOperationQrDisplayValue}
      qrValue={getOperationQrValue}
      showQrColumn={false}
      transformPayload={(payload, editData) => {
        if (!editData) return payload;
        const updatePayload = { ...payload };
        delete updatePayload.plant_id;
        return updatePayload;
      }}
      onOpenForm={() => {
        loadPlantOptions();
        loadRelationTypes();
      }}
      noDataLabel="No operations found"
    />
  );
};

export default Operation;
